/**
 * HITLRenderer - Unified Human-in-the-Loop Rendering System
 *
 * Routes to the appropriate renderer based on HITLResponse.kind.
 * Copied from extension with OAuthRenderer adapted for web (window.open instead of chrome.tabs).
 */

import { HITLResponse, HITLKind, HITLSubmitResult, normalizeHITLResponse } from '@shared/types/hitl';
import { SingleChoiceRenderer } from './renderers/SingleChoiceRenderer';
import { MultipleChoiceRenderer } from './renderers/MultipleChoiceRenderer';
import { TextInputRenderer } from './renderers/TextInputRenderer';
import { YesNoRenderer } from './renderers/YesNoRenderer';
import { FormRenderer } from './renderers/FormRenderer';
import { CRMPreviewRenderer } from './renderers/CRMPreviewRenderer';
import { OAuthRenderer } from './renderers/OAuthRenderer';
import { CaptureRenderer } from './renderers/CaptureRenderer';
import { ConfirmationRenderer } from './renderers/ConfirmationRenderer';
import { FallbackRenderer } from './renderers/FallbackRenderer';

const hasValidationFieldErrors = (response: HITLResponse): boolean => {
  const fieldErrors = response.formValidation?.fieldErrors;
  return !!fieldErrors && Object.keys(fieldErrors).length > 0;
};

export interface HITLRendererProps {
  response: HITLResponse;
  onSubmit: (answer: unknown) => void | Promise<void | HITLSubmitResult>;
  onCancel?: () => void;
  onSpecialAction?: (action: string, metadata?: Record<string, unknown>) => Promise<void>;
  isSubmitting?: boolean;
  compact?: boolean;
}

const RENDERERS: Record<HITLKind, React.FC<HITLRendererProps>> = {
  single_choice: SingleChoiceRenderer,
  multiple_choice: MultipleChoiceRenderer,
  text: TextInputRenderer,
  yes_no: YesNoRenderer,
  rich_text: TextInputRenderer,
  form: FormRenderer,
  crm_preview: CRMPreviewRenderer,
  confirmation: ConfirmationRenderer,
  oauth: OAuthRenderer,
  capture: CaptureRenderer,
};

interface HITLRendererContainerProps {
  response: HITLResponse | Record<string, unknown>;
  onSubmit: (answer: unknown) => void | Promise<void | HITLSubmitResult>;
  onCancel?: () => void;
  onSpecialAction?: (action: string, metadata?: Record<string, unknown>) => Promise<void>;
  isSubmitting?: boolean;
  compact?: boolean;
}

export const HITLRenderer: React.FC<HITLRendererContainerProps> = ({
  response,
  onSubmit,
  onCancel,
  onSpecialAction,
  isSubmitting = false,
  compact = false,
}) => {
  const normalized: HITLResponse | null =
    'kind' in response && typeof response.kind === 'string'
      ? response as HITLResponse
      : normalizeHITLResponse(response as Record<string, unknown>);

  if (!normalized) return null;

  const effectiveResponse: HITLResponse = (
    normalized.kind === 'confirmation'
    && hasValidationFieldErrors(normalized)
  )
    ? {
      ...normalized,
      kind: 'form',
      formData: normalized.formData ?? normalized.formValidation?.normalizedData ?? {},
      message: normalized.formValidation?.message ?? normalized.message,
      formTitle: normalized.formTitle ?? normalized.intentLabel ?? 'Corriger les informations',
      formDescription: normalized.formDescription ?? 'Corrigez les champs en erreur puis continuez.',
    }
    : normalized;

  const Renderer = RENDERERS[effectiveResponse.kind] ?? FallbackRenderer;

  return (
    <Renderer
      response={effectiveResponse}
      onSubmit={onSubmit}
      onCancel={onCancel}
      onSpecialAction={onSpecialAction}
      isSubmitting={isSubmitting}
      compact={compact}
    />
  );
};

export default HITLRenderer;
